import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  type SessionLogRecord,
  type SessionDetailsResponse,
  getCarrierDownloadSessionDetails,
} from '../services/carrierDownloadService';

export interface CarrierSessionPanel {
  row: SessionLogRecord;
  details: SessionDetailsResponse | null;
  loading: boolean;
  error: string | null;
}

interface CarrierSessionContextValue {
  sessionPanel: CarrierSessionPanel | null;
  openSession: (row: SessionLogRecord) => void;
  closeSession: () => void;
}

const CarrierSessionContext = createContext<CarrierSessionContextValue>({
  sessionPanel: null,
  openSession: () => {},
  closeSession: () => {},
});

export const CarrierSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessionPanel, setSessionPanel] = useState<CarrierSessionPanel | null>(null);

  const openSession = useCallback((row: SessionLogRecord) => {
    setSessionPanel({ row, details: null, loading: true, error: null });
    getCarrierDownloadSessionDetails(row.databaseId, row.consumedRows)
      .then(details =>
        setSessionPanel(prev =>
          prev?.row.databaseId === row.databaseId ? { ...prev, details, loading: false } : prev
        )
      )
      .catch(err =>
        setSessionPanel(prev =>
          prev?.row.databaseId === row.databaseId
            ? { ...prev, error: err?.message ?? 'Failed to load session details.', loading: false }
            : prev
        )
      );
  }, []);

  const closeSession = useCallback(() => setSessionPanel(null), []);

  return (
    <CarrierSessionContext.Provider value={{ sessionPanel, openSession, closeSession }}>
      {children}
    </CarrierSessionContext.Provider>
  );
};

export const useCarrierSession = () => useContext(CarrierSessionContext);
